import React from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { FormattedDate } from 'react-intl';
import { Button } from '..';
import { TripState } from '../../types/trip.store.type';
import { IconExclamation } from '../../assets/icons';
import './RequiredActionsModal.scss';
import { AppRoutes } from '../../routes';
import { ReservationUtils, TranslationUtils } from '../../utils';

interface RequiredActionsModalProps {
    show: boolean;
    handleClose: () => any;
}

export const RequiredActionsModal: React.FC<RequiredActionsModalProps> = (props) => {
    const { show, handleClose } = props;
    const history = useHistory();
    const { required_actions: requiredActions, stay, reservation } = useSelector((store: { trip: TripState }) => store.trip);

    if (!requiredActions || !stay || !reservation) {
        return null;
    }

    const { contract_agreement, pay_balance, vehicle_registration } = requiredActions;

    const isAgreementComplete = contract_agreement && contract_agreement.completed;
    const isPaymentComplete = pay_balance && pay_balance.completed;
    const shouldIgnoreBalance = ReservationUtils.shouldIgnoreBalance(reservation);
    const isVehicleRegistrationComplete = vehicle_registration && vehicle_registration.completed;

    const requiredBy = <FormattedDate value={ReservationUtils.getRequiredByDate(stay)} month="long" day="numeric" year="numeric" />;

    const balanceDueBy = (
        <FormattedDate value={ReservationUtils.getBalanceDueDate(stay, reservation)} month="long" day="numeric" year="numeric" />
    );

    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton />

            <div className="required-modal-body">
                <div className="required-modal-title">
                    <div className="required-modal-title-icon">
                        <IconExclamation />
                    </div>
                    <h4 className={'heading-med-light'}>{TranslationUtils.get('finalize_your_reservation')}</h4>
                </div>

                <div className="required-modal-text">
                    <p className={'body-regular'}>{TranslationUtils.get('these_actions_are_required')}</p>
                </div>
                <div className="divider-column">
                    {!isAgreementComplete && (
                        <div>
                            <strong className={'body-bold'}>{TranslationUtils.get('sign_agreement_by', { signBy: requiredBy })}</strong>
                            <Button
                                id="body-regular button-required-action-modal-agreement"
                                className="action-button"
                                onClick={() => {
                                    history.push(AppRoutes.RENTAL_AGREEMENT);
                                }}
                                label={TranslationUtils.get('sign_agreement')}
                                variant="info"
                                block
                            />
                        </div>
                    )}

                    {!isPaymentComplete && !shouldIgnoreBalance && (
                        <div>
                            <strong className="body-bold required-action-modal-payment-due-text">
                                {TranslationUtils.get('pay_balance_by', { payBy: balanceDueBy })}
                            </strong>
                            <Button
                                id="button-required-action-modal-payment"
                                className="body-regular action-button"
                                onClick={() => {
                                    history.push(AppRoutes.PAYMENT);
                                }}
                                label={TranslationUtils.get('pay_balance')}
                                variant="info"
                                block
                            />
                        </div>
                    )}

                    {vehicle_registration && !isVehicleRegistrationComplete && (
                        <div>
                            <strong className={'body-bold'}>
                                {TranslationUtils.get('register_vehicles_by', { registerBy: requiredBy })}
                            </strong>
                            <Button
                                id="button-required-action-modal-vehicle-registration"
                                className="body-regular action-button"
                                onClick={() => {
                                    history.push(AppRoutes.VEHICLE_REGISTRATION);
                                }}
                                label={TranslationUtils.get('register_vehicle')}
                                variant="info"
                                block
                            />
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};
